// ---------------------------------------------------------------------------
// Meta (Facebook/Instagram) pixel loader. The official base snippet, written
// as a typed function instead of an inline <script> string, so the pixel id is
// never interpolated into executable code and the loader is unit-testable.
//
// Callers (MetaPixel.tsx) are responsible for consent: this file loads the
// pixel unconditionally when called.
// ---------------------------------------------------------------------------

export const META_PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js"

type FbqStub = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: FbqStub
  loaded: boolean
  version: string
}

/** Meta pixel ids are numeric (15-16 digits today). Anything else is a config error. */
export function isValidMetaPixelId(id: string | undefined | null): id is string {
  return typeof id === "string" && /^\d{6,20}$/.test(id)
}

/**
 * Installs the fbq command queue, requests fbevents.js and runs `init`.
 * Returns true when this call installed it, false when fbq already existed
 * (a client-side navigation or a second consent grant must not re-init).
 */
export function installMetaPixel(pixelId: string): boolean {
  if (typeof window === "undefined" || !isValidMetaPixelId(pixelId)) return false
  if (typeof window.fbq === "function") return false

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  } as FbqStub
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = "2.0"
  fbq.queue = []
  window.fbq = fbq
  ;(window as unknown as { _fbq?: FbqStub })._fbq ??= fbq

  const script = document.createElement("script")
  script.async = true
  script.src = META_PIXEL_SRC
  document.head.appendChild(script)

  fbq("init", pixelId)
  return true
}
